import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  User, LogOut, Loader2, Sparkles, Star, ChevronRight, ChevronLeft, CheckCircle, Camera, Send, X, 
  Calendar, Orbit, RefreshCw, Activity, Phone, Save, Settings, MapPin, History, 
  CreditCard, Tag, AlertCircle, AlertTriangle, Info, Clock, Search, Check, Layers, 
  BarChart3, Globe, Zap, Timer, Video, MessageCircle, MessageSquare, XCircle, 
  ArrowRight, ShoppingCart, PhoneCall, Trash2, ArrowUpCircle, Plus, Users, Heart, 
  Sparkle, Moon, Sun, Bell, ArrowLeft, Lock, Play, FileText, StopCircle, ExternalLink, ShieldCheck,
  Minus, Wallet, UploadCloud, Gem, Flame, SkipForward, HelpCircle, Mic, BookOpen, Compass, Edit2
} from 'lucide-react';
import { 
  findMemberByEmail, fetchMessages, sendMessage, updateMemberProfile, 
  calculateBirthChart, getPersonalizedHoroscope, fetchMemberBookings, 
  suggestLocations, addCredits, updateMemberTier, addBond, deleteBond, updateBond,
  calculateCompatibility, supabase, getAdminStatus, fetchExclusiveContent, 
  unlockContent, fetchSessionLogs, deductCredits 
} from '../services/geminiService';
import { MemberRecord, Message, ChartResult, Bond, CompatibilityResult, ExclusiveContent, BookingRecord, PlanetaryPlacement, HousePlacement, BirthData, LocationSuggestion, Language, TutorialStep } from '../types';
import PayPalButtons from './PayPalButtons';
import LiveVideoRoom from './LiveVideoRoom';
import VertexFortuneCalculator from './VertexFortuneCalculator';
import { translations } from '../translations';

interface PresencePayload {
  status: 'online' | 'away';
}

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂', 
  Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇'
};

const ZODIAC_ORDER = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const ZODIAC_SYMBOLS: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓'
};

const PLANET_ARCHETYPES: Record<string, { title: string, desc: string, keyword: string }> = {
  Sun: { title: "The Core Self", desc: "Governs your ego, identity, and the light you shine into the world. It is the heart of your being.", keyword: "VITALITY" },
  Moon: { title: "The Emotional Compass", desc: "Governs your inner world, subconscious, and how you feel and nurture yourself.", keyword: "FEELING" },
  Mercury: { title: "The Messenger", desc: "Governs communication, logic, and the way your mind processes information.", keyword: "INTELLECT" },
  Venus: { title: "The Harmonizer", desc: "Governs love, beauty, money, and what you value in relationships and life.", keyword: "VALUE" },
  Mars: { title: "The Warrior", desc: "Governs your drive, energy, passion, and how you assert yourself to get what you want.", keyword: "ACTION" },
  Jupiter: { title: "The Great Expander", desc: "Governs luck, abundance, growth, and your philosophical search for meaning.", keyword: "EXPANSION" },
  Saturn: { title: "The Taskmaster", desc: "Governs structure, discipline, boundaries, and the long-term lessons you must learn.", keyword: "DISCIPLINE" },
  Uranus: { title: "The Awakener", desc: "Governs sudden change, innovation, rebellion, and your unique genius.", keyword: "REVOLUTION" },
  Neptune: { title: "The Dreamer", desc: "Governs spirituality, dreams, illusion, and the dissolution of boundaries between worlds.", keyword: "SPIRIT" },
  Pluto: { title: "The Alchemist", desc: "Governs transformation, death, rebirth, and the power hidden in the shadows.", keyword: "POWER" }
};

const HOUSE_ARENAS: Record<number, { title: string, desc: string, domain: string }> = {
  1: { title: "The House of Self", desc: "The lens through which you see the world. Your physical appearance and first impressions.", domain: "IDENTITY" },
  2: { title: "The House of Value", desc: "Your personal finances, material possessions, and your sense of self-worth.", domain: "RESOURCES" },
  3: { title: "The House of Mind", desc: "Local travel, siblings, early education, and your everyday style of communication.", domain: "EXPRESSION" },
  4: { title: "The House of Home", desc: "Your family, roots, emotional foundations, and your private sanctuary.", domain: "ROOTS" },
  5: { title: "The House of Joy", desc: "Creativity, romance, children, play, and taking risks from the heart.", domain: "PLEASURE" },
  6: { title: "The House of Service", desc: "Daily routines, physical health, work habits, and your relationship to service.", domain: "WELLNESS" },
  7: { title: "The House of Others", desc: "One-on-one partnerships, marriage, and how you relate to the 'other'.", domain: "UNION" },
  8: { title: "The House of Shadow", desc: "Shared resources, deep transformation, intimacy, and the mysteries of life and death.", domain: "MERGER" },
  9: { title: "The House of Truth", desc: "Higher education, long-distance travel, philosophy, and your search for wisdom.", domain: "BELIEF" },
  10: { title: "The House of Status", desc: "Career, public reputation, legacy, and your highest worldly achievements.", domain: "MISSION" },
  11: { title: "The House of Community", desc: "Friendships, collective groups, hopes, and your vision for the future.", domain: "VISION" },
  12: { title: "The House of Soul", desc: "The subconscious, hidden strengths, karma, and the spiritual path beyond the physical.", domain: "TRANSCENDENCE" }
};

const LOADING_STATUSES = [
  "Synchronizing with Celestial Epochs...",
  "Retrieving High-Precision Coordinates...",
  "Executing Astronomical Trigonometry...",
  "Applying Placidus House Mathematics...",
  "Verifying Planetary Arc-Minutes...",
  "Persisting Alignment to Soul Registry..."
];

interface MemberPortalProps {
  onExit: () => void;
  onLogin: (member: MemberRecord) => void;
  onUpdateMember: (member: MemberRecord) => void;
  onBookReading: () => void;
  initialMember: MemberRecord | null;
  lang: Language;
}

const MiniChartWidget: React.FC<{ chart: ChartResult, lang: Language }> = ({ chart, lang }) => {
  const t = translations[lang] || translations.en;
  
  const bigThree = [
    { label: 'Sun', sign: chart.sunSign, icon: Sun, color: 'text-amber-500' },
    { label: 'Moon', sign: chart.moonSign, icon: Moon, color: 'text-indigo-400' },
    { label: 'Rising', sign: chart.risingSign, icon: Orbit, color: 'text-emerald-400' }
  ];

  return (
    <div id="portal-mini-chart" className="bg-slate-950/60 border border-indigo-900/50 rounded-sm p-6 flex flex-col items-center">
       <div className="flex items-center justify-between w-full mb-6">
          <h4 className="text-amber-500 text-[9px] uppercase font-bold tracking-[0.3em] flex items-center gap-2">
            <Sparkles size={12}/> {t.portalHome.miniChartTitle}
          </h4>
          <div className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center gap-1.5">
             <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_#10b981]" />
             <span className="text-[8px] uppercase font-bold text-emerald-400 tracking-widest">Fixed Alignment</span>
          </div>
       </div>

       <div className="relative w-40 h-40 mb-8 group">
          <div className="absolute inset-0 bg-amber-500/5 blur-2xl rounded-full" />
          <svg viewBox="0 0 100 100" className="w-full h-full relative z-10 animate-[spin_120s_linear_infinite]">
             <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(245,158,11,0.1)" strokeWidth="0.5" strokeDasharray="2 4" />
             <circle cx="50" cy="50" r="35" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="0.5" />
             {[...Array(12)].map((_, i) => (
                <line key={i} x1="50" y1="10" x2="50" y2="5" stroke="rgba(245,158,11,0.2)" strokeWidth="0.5" transform={`rotate(${i * 30}, 50, 50)`} />
             ))}
             {bigThree.map((item, idx) => {
               const angle = (ZODIAC_ORDER.indexOf(item.sign) * 30) + 15;
               return (
                 <g key={idx} transform={`rotate(${angle}, 50, 50)`}>
                    <circle cx="50" cy="15" r="3" fill="currentColor" className={item.color} />
                    <text x="50" y="24" fontSize="6" textAnchor="middle" fill="white" className="font-serif font-bold" transform={`rotate(${-angle}, 50, 24)`}>{ZODIAC_SYMBOLS[item.sign]}</text>
                 </g>
               );
             })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
             <Orbit size={32} className="text-amber-500/40" />
          </div>
       </div>

       <div className="grid grid-cols-3 gap-4 w-full">
          {bigThree.map((item, i) => (
            <div key={i} className="text-center space-y-1">
               <p className="text-[7px] uppercase text-indigo-500 font-bold tracking-widest">{item.label}</p>
               <item.icon size={16} className={`${item.color} mx-auto`} />
               <p className="text-[10px] text-white font-serif whitespace-nowrap">{item.sign}</p>
            </div>
          ))}
       </div>
    </div>
  );
};

const MemberPortal: React.FC<MemberPortalProps> = ({ onExit, onLogin, onUpdateMember, onBookReading, initialMember, lang }) => {
  const [member, setMember] = useState<MemberRecord | null>(initialMember);
  const [email, setEmail] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  
  const [memberChart, setMemberChart] = useState<ChartResult | null>(member?.calculated_chart || null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [alignmentError, setAlignmentError] = useState<string | null>(null);
  const [isSyncingWithRegistry, setIsSyncingWithRegistry] = useState(false);
  
  // FIXED: Ensure immediate transition if chart data is already loaded.
  const [proceedToPortal, setProceedToPortal] = useState(!!member?.calculated_chart); 
  
  const [loadingStatusIndex, setLoadingStatusIndex] = useState(0);

  const [horoscopes, setHoroscopes] = useState({ daily: '', weekly: '', yearly: '' });
  const [activeTab, setActiveTab] = useState<'home' | 'my-chart' | 'messages' | 'profile' | 'bookings' | 'vault' | 'bonds'>('home');
  const [chartSubTab, setChartSubTab] = useState<'planets' | 'houses'>('planets');
  const [activePlacement, setActivePlacement] = useState<{ type: 'planet' | 'house', data: any } | null>(null);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [memberBookings, setMemberBookings] = useState<BookingRecord[]>([]);
  const [adminStatus, setAdminStatus] = useState<'online' | 'away'>('away');
  const [activeCall, setActiveCall] = useState<{ type: 'audio' | 'video' } | null>(null);

  const [exclusiveItems, setExclusiveItems] = useState<ExclusiveContent[]>([]);
  const [loadingExclusive, setLoadingExclusive] = useState(false);

  const [isAddingBond, setIsAddingBond] = useState(false);
  const [editingBondId, setEditingBondId] = useState<string | null>(null);
  const [bondForm, setBondForm] = useState({ 
    name: '', birth_date: '', birth_time: '', birth_location: '', relationship: '',
    lat: undefined as number | undefined, lng: undefined as number | undefined
  });
  const [bondLocationSuggestions, setBondLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearchingBondLocation, setIsSearchingBondLocation] = useState(false);
  const [showBondSuggestions, setShowBondSuggestions] = useState(false);
  const preventNextBondSuggestion = useRef(false);

  const [activeBond, setActiveBond] = useState<Bond | null>(null);
  const [activeBondChart, setActiveBondChart] = useState<ChartResult | null>(null);
  const [loadingBondChart, setLoadingBondChart] = useState(false);

  const t = translations[lang] || translations.en;

  const hasUnlockedChat = useMemo(() => {
    return memberBookings.some(b => b.status === 'paid' || b.status === 'completed');
  }, [memberBookings]);

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '', birth_date: '', birth_time: '', birth_location: '', location: '', bio: '',
    notify_transits: true, notify_marketing: false, lat: undefined as number | undefined, lng: undefined as number | undefined
  });

  // Location suggestions for Bond form
  useEffect(() => {
    if (!isAddingBond || preventNextBondSuggestion.current) {
      preventNextBondSuggestion.current = false;
      return;
    }
    const timer = setTimeout(async () => {
      if (bondForm.birth_location && bondForm.birth_location.length > 3 && !isSearchingBondLocation) {
        setIsSearchingBondLocation(true);
        const results = await suggestLocations(bondForm.birth_location);
        setBondLocationSuggestions(results);
        setShowBondSuggestions(results.length > 0);
        setIsSearchingBondLocation(false);
      } else if (!bondForm.birth_location || bondForm.birth_location.length <= 3) {
        setBondLocationSuggestions([]);
        setShowBondSuggestions(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [bondForm.birth_location, isAddingBond]);

  // Re-sync local state if initialMember prop changes from App.tsx
  useEffect(() => {
    if (initialMember) {
      setMember(initialMember);
      if (initialMember.calculated_chart) {
        setMemberChart(initialMember.calculated_chart);
        setProceedToPortal(true);
      }
    }
  }, [initialMember]);

  useEffect(() => {
    if (member) {
      setProfileForm({
        name: member.name || '',
        birth_date: member.birth_date || '',
        birth_time: member.birth_time || '',
        birth_location: member.birth_location || '',
        location: member.location || '',
        bio: member.bio || '',
        notify_transits: member.settings?.notify_transits ?? true,
        notify_marketing: member.settings?.notify_marketing ?? false,
        lat: member.lat,
        lng: member.lng
      });
    }
  }, [member]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => { if (activeTab === 'messages') scrollToBottom(); }, [messages, activeTab]);

  useEffect(() => {
    if (loadingChart && !memberChart) {
      const interval = setInterval(() => {
        setLoadingStatusIndex((prev) => (prev + 1) % LOADING_STATUSES.length);
      }, 2000); 
      return () => clearInterval(interval);
    }
  }, [loadingChart, memberChart]);

  const loadData = useCallback(async (identifier: string) => {
    setLoadingExclusive(true);
    try {
      const [msgs, status, bookings, exclusive] = await Promise.all([
        fetchMessages(identifier),
        getAdminStatus(),
        fetchMemberBookings(identifier),
        fetchExclusiveContent()
      ]);
      setMessages(msgs);
      setAdminStatus(status);
      setMemberBookings(bookings);
      setExclusiveItems(exclusive);
    } catch (err) { console.error("Portal load fail", err); }
    finally { setLoadingExclusive(false); }
  }, []);

  const fetchHoroscope = async (memberData: MemberRecord) => {
    if (!memberData || !memberData.birth_date) return;
    try {
      const birthData: BirthData = {
        name: memberData.name, date: memberData.birth_date,
        time: memberData.birth_time || '12:00', location: memberData.birth_location || 'Unknown'
      };
      const [daily, weekly] = await Promise.all([
        getPersonalizedHoroscope(birthData, 'daily', lang),
        getPersonalizedHoroscope(birthData, 'weekly', lang)
      ]);
      setHoroscopes(prev => ({ ...prev, daily, weekly }));
    } catch (e) { console.error("Horoscope fail", e); }
  };

  const fetchMyChart = async (customData?: MemberRecord) => {
    const dataToUse = customData || member;
    if (!dataToUse || !dataToUse.birth_date) return;
    
    // Skip calculation ritual if chart exists AND we are NOT explicitly forcing a refresh
    if (!customData && dataToUse.calculated_chart) {
      setMemberChart(dataToUse.calculated_chart);
      setProceedToPortal(true);
      return;
    }

    setProceedToPortal(false); 
    setLoadingChart(true);
    setAlignmentError(null);
    try {
      const birthData: BirthData = {
        name: dataToUse.name, 
        date: dataToUse.birth_date,
        time: dataToUse.birth_time || '12:00', 
        location: dataToUse.birth_location || 'Unknown',
        lat: dataToUse.lat,
        lng: dataToUse.lng
      };
      
      const chart = await calculateBirthChart(birthData, lang);
      
      // IMMEDIATE UI UPDATE: Show the chart first to prevent perceived "Interruption"
      setMemberChart(chart);
      setProceedToPortal(true);

      // NON-BLOCKING PERSISTENCE: Save to Supabase in the background
      if (member) {
        setIsSyncingWithRegistry(true);
        updateMemberProfile(member.id, { calculated_chart: chart })
          .then(() => {
            const updatedRecord = { ...member, calculated_chart: chart };
            setMember(updatedRecord);
            onUpdateMember(updatedRecord);
            console.log("Registry sync successful.");
          })
          .catch((err) => {
            console.warn("Registry sync deferred:", err);
          })
          .finally(() => {
            setIsSyncingWithRegistry(false);
          });
      }
    } catch (err) {
      console.error("Celestial calculation failed:", err);
      setAlignmentError("Calibration Interrupted.");
    } finally {
      setLoadingChart(false);
    }
  };

  useEffect(() => {
    if (member) {
      const identifier = member.email || member.id;
      loadData(identifier);
      fetchHoroscope(member);
      
      // Check for saved chart or calculate if missing
      if (!member.calculated_chart && member.birth_date) {
        fetchMyChart();
      } else if (member.calculated_chart) {
        setMemberChart(member.calculated_chart);
        setProceedToPortal(true);
      }

      const presenceChannel = supabase.channel('presence_sync')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_presence', filter: 'id=eq.allie_main' }, (payload) => {
          const newData = payload.new as PresencePayload;
          if (newData && newData.status) setAdminStatus(newData.status);
        }).subscribe();

      return () => { supabase.removeChannel(presenceChannel); };
    }
  }, [member?.id, lang]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoggingIn(true);
    setError('');
    try {
      const res = await findMemberByEmail(email);
      if (res) { 
        setMember(res); 
        onLogin(res); 
      } 
      else { setError("Registry entry not found."); }
    } catch (err) { setError("Connection error."); }
    finally { setIsLoggingIn(false); }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;
    setIsSavingProfile(true);
    try {
      const updates: Partial<MemberRecord> = {
        name: profileForm.name,
        birth_date: profileForm.birth_date,
        birth_time: profileForm.birth_time,
        birth_location: profileForm.birth_location,
        location: profileForm.location,
        bio: profileForm.bio,
        settings: {
          comm_preference: member.settings?.comm_preference || 'video',
          notify_transits: profileForm.notify_transits,
          notify_marketing: profileForm.notify_marketing
        },
        lat: profileForm.lat,
        lng: profileForm.lng
      };
      
      const success = await updateMemberProfile(member.id, updates);
      if (success) {
        const updatedMember = { ...member, ...updates };
        setMember(updatedMember);
        onUpdateMember(updatedMember);
        
        // If birth date changed, we MUST clear the persisted chart and re-calculate
        if (updates.birth_date !== member.birth_date) {
          setProceedToPortal(false);
          await fetchMyChart(updatedMember);
        }
        alert("Soul coordinates synchronized.");
      }
    } catch (err) { alert("Failed to update registry."); }
    finally { setIsSavingProfile(false); }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !member || sendingMessage) return;
    setSendingMessage(true);
    const msg = { sender_id: member.email || member.id, receiver_id: 'admin', content: newMessage };
    try {
      await sendMessage(msg);
      setMessages(prev => [...prev, { ...msg, id: Date.now().toString(), created_at: new Date().toISOString() } as Message]);
      setNewMessage('');
    } catch (err) { alert("Transmission failed."); }
    finally { setSendingMessage(false); }
  };

  const handleBondFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;
    
    if (editingBondId) {
      try {
        const res = await updateBond(member.id, editingBondId, bondForm);
        if (res.success) {
          const updatedBonds = (member.bonds || []).map(b => b.id === editingBondId ? { ...b, ...bondForm } : b);
          const updatedMember = { ...member, bonds: updatedBonds };
          setMember(updatedMember);
          onUpdateMember(updatedMember);
          setIsAddingBond(false);
          setEditingBondId(null);
          setBondForm({ name: '', birth_date: '', birth_time: '', birth_location: '', relationship: '', lat: undefined, lng: undefined });
        }
      } catch (err) { alert("Bond modification failed."); }
    } else {
      if (bondForm.lat === undefined) {
        alert("Please select a location from the suggestions to ensure lat/lng accuracy.");
        return;
      }
      try {
        const res = await addBond(member.id, bondForm);
        if (res.success) {
          const updatedBonds = [res.bond as Bond, ...(member.bonds || [])];
          const updatedMember = { ...member, bonds: updatedBonds };
          setMember(updatedMember);
          onUpdateMember(updatedMember);
          setIsAddingBond(false);
          setBondForm({ name: '', birth_date: '', birth_time: '', birth_location: '', relationship: '', lat: undefined, lng: undefined });
        }
      } catch (err) { alert("Bond formation failed."); }
    }
  };

  const handleDeleteBond = async (id: string) => {
    if (!member || !confirm("Sever this celestial bond?")) return;
    try {
      const res = await deleteBond(member.id, id);
      if (res.success) {
        const updatedBonds = (member.bonds || []).filter(b => b.id !== id);
        const updatedMember = { ...member, bonds: updatedBonds };
        setMember(updatedMember);
        onUpdateMember(updatedMember);
      }
    } catch (err) { alert("Bond remains intact."); }
  };

  const handleEditBond = (bond: Bond) => {
    setEditingBondId(bond.id);
    setBondForm({
      name: bond.name,
      birth_date: bond.birth_date,
      birth_time: bond.birth_time,
      birth_location: bond.birth_location,
      relationship: bond.relationship || '',
      lat: bond.lat,
      lng: bond.lng
    });
    setIsAddingBond(true);
  };

  const handleInspectBond = async (bond: Bond) => {
    setActiveBond(bond);
    setActiveBondChart(null);
    setLoadingBondChart(true);
    try {
      const birthData: BirthData = {
        name: bond.name,
        date: bond.birth_date,
        time: bond.birth_time,
        location: bond.birth_location,
        lat: bond.lat,
        lng: bond.lng
      };
      const chart = await calculateBirthChart(birthData, lang);
      setActiveBondChart(chart);
    } catch (err) {
      alert("Resonance calculation failed.");
    } finally {
      setLoadingBondChart(false);
    }
  };

  const tabs = [
    { id: 'home', label: t.common.home || 'Home', icon: Globe },
    { id: 'my-chart', label: t.common.chart || 'Chart', icon: BarChart3 },
    { id: 'vault', label: t.common.vault || 'Vault', icon: Lock },
    { id: 'bonds', label: 'Bonds', icon: Heart },
    { id: 'messages', label: t.common.chat || 'Chat', icon: MessageSquare },
    { id: 'bookings', label: t.common.book || 'Bookings', icon: Calendar },
    { id: 'profile', label: t.common.settings || 'Settings', icon: Settings },
  ];

  if (!member) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="w-full max-md bg-slate-900 border border-amber-500/20 p-10 rounded-sm text-center shadow-2xl animate-fade-in">
          <Sparkles className="mx-auto mb-8 text-amber-500 animate-pulse" size={40} />
          <h2 className="text-3xl font-serif text-white mb-6 uppercase tracking-widest leading-tight">Member Sanctuary</h2>
          <form onSubmit={handleLogin} className="space-y-6 text-left">
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-indigo-400 font-bold ml-1">Registry Email</label>
              <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="email@example.com" />
            </div>
            {error && <p className="text-rose-500 text-[10px] uppercase font-bold text-center bg-rose-500/10 py-2 rounded-sm border border-rose-500/20">{error}</p>}
            <button disabled={isLoggingIn} type="submit" className="w-full py-5 bg-amber-500 text-slate-950 font-bold uppercase text-[10px] tracking-widest hover:bg-amber-400 transition-all rounded-sm shadow-lg active:scale-95 flex items-center justify-center gap-2">
              {isLoggingIn ? <Loader2 className="animate-spin" size={16} /> : <><Lock size={14}/> Authorize Entry</>}
            </button>
          </form>
          <button onClick={onExit} className="mt-8 text-indigo-500 text-[10px] uppercase tracking-[0.3em] hover:text-white transition-colors">Back to Realm</button>
        </div>
      </div>
    );
  }

  // RITUAL SCREEN: Processing logic for new mappings.
  if (!proceedToPortal && (loadingChart || alignmentError)) {
    return (
      <div className="fixed inset-0 z-[200000] bg-[#020617] flex flex-col items-center justify-center p-8 text-center animate-fade-in">
        <div className="relative mb-6 flex flex-col items-center justify-center">
          <div className="absolute w-[300px] h-[300px] bg-amber-500/5 rounded-full blur-[100px] animate-pulse" />
          <svg className="absolute w-48 h-48 md:w-64 md:h-64 -rotate-90">
            <circle cx="50%" cy="50%" r="48%" fill="none" stroke="rgba(245,158,11,0.05)" strokeWidth="1" />
            <circle cx="50%" cy="50%" r="48%" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="188.49" strokeDashoffset={0} className="animate-pulse shadow-[0_0_10px_#f59e0b]" />
          </svg>
          <div className="relative w-40 h-40 md:w-56 md:h-56 rounded-full border border-amber-500/10 flex items-center justify-center animate-[spin_10s_linear_infinite]">
             <svg viewBox="0 0 100 100" className="w-full h-full p-6 text-amber-500/40">
                <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 4" />
                <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.5" />
                {[...Array(12)].map((_, i) => (<line key={i} x1="50" y1="10" x2="50" y2="5" stroke="currentColor" strokeWidth="0.5" transform={`rotate(${i * 30}, 50, 50)`} />))}
             </svg>
          </div>
        </div>

        <div className="space-y-8 animate-fade-in flex flex-col items-center max-w-xl mx-auto relative z-10">
          <div className="space-y-4 text-center">
            <h2 className="text-2xl md:text-4xl font-serif text-white tracking-widest uppercase leading-tight">Instant Mapping</h2>
            <div className="h-px w-32 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent mx-auto" />
            <div className="h-16 flex flex-col items-center justify-center">
              {alignmentError ? (
                <div className="flex flex-col items-center gap-4">
                  <span className="text-rose-400 text-xs md:text-sm uppercase font-bold tracking-widest">Calibration Interrupted</span>
                  <button onClick={() => fetchMyChart()} className="px-6 py-2 border border-amber-500 text-amber-500 text-[10px] uppercase font-bold tracking-widest rounded-sm">Retry Alignment</button>
                </div>
              ) : (
                <p className="text-indigo-200/60 text-xs md:text-base font-light italic leading-relaxed tracking-wide text-center mx-auto max-w-md animate-fade-in" key={loadingStatusIndex}>"{LOADING_STATUSES[loadingStatusIndex]}"</p>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-center gap-8 animate-fade-in">
             <div className="flex items-center gap-3 px-8 py-3 bg-indigo-950/40 border border-indigo-900/50 rounded-full text-indigo-300 text-[10px] uppercase tracking-[0.3em] font-bold shadow-2xl">
               <Loader2 size={12} className="animate-spin text-amber-500" /> High-Speed Synchronizing...
             </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col h-screen overflow-hidden animate-fade-in relative">
      {activeCall && (
        <LiveVideoRoom isAdmin={false} memberEmail={member.email || member.id} audioOnly={activeCall.type === 'audio'} onClose={() => setActiveCall(null)} />
      )}

      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-indigo-900/30 p-3 md:p-4 flex justify-between items-center px-4 md:px-8 shrink-0 relative z-[100]">
        <div id="portal-user-profile" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full border border-amber-500/30 overflow-hidden bg-indigo-950 flex items-center justify-center">
            {member.profile_picture ? (
              <img src={member.profile_picture} alt={member.name} className="w-full h-full object-cover" />
            ) : ( <User size={20} className="text-amber-500/50" /> )}
          </div>
          <div>
            <h1 className="text-sm font-serif text-white tracking-widest uppercase">{member.name.split(' ')[0]}</h1>
            <div className="flex items-center gap-2">
              <p className="text-[7px] text-amber-500 uppercase tracking-widest font-bold">{member.tier}</p>
              {member.calculated_chart && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_#10b981]" title="Soul Map Persisted" />}
            </div>
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-4 overflow-x-auto no-scrollbar">
          {tabs.map(tab => (
            <button 
              key={tab.id} 
              id={`portal-nav-${tab.id}`} 
              onClick={() => { setActiveTab(tab.id as any); setActivePlacement(null); setActiveBond(null); }} 
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-amber-500 text-slate-950 font-bold shadow-lg' : 'text-indigo-400 hover:text-white hover:bg-indigo-900/30'}`}
            >
              <tab.icon size={12} /><span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <div id="portal-admin-status" className="hidden sm:flex flex-col items-end">
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${adminStatus === 'online' ? 'bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]' : 'bg-rose-500'}`} />
              <span className={`text-[8px] uppercase tracking-widest font-bold ${adminStatus === 'online' ? 'text-emerald-400' : 'text-indigo-400'}`}>Allie: {adminStatus === 'online' ? t.chat.online : t.chat.offline}</span>
            </div>
          </div>
          <button id="portal-logout-btn" onClick={() => { setMember(null); onExit(); }} className="p-2 text-indigo-400 hover:text-rose-500 transition-colors"><LogOut size={18} /></button>
        </div>
      </header>

      {/* PORTAL MOBILE BOTTOM NAV */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000001] bg-[#020617] border-t border-amber-500/40 shadow-[0_-8px_30px_rgba(0,0,0,0.9)] pb-[env(safe-area-inset-bottom, 0)] h-20 flex justify-around items-center px-2">
        {tabs.slice(0, 5).map((tab) => (
          <button 
            key={tab.id} 
            onClick={() => { setActiveTab(tab.id as any); setActivePlacement(null); setActiveBond(null); }} 
            className={`flex flex-col items-center justify-center gap-1.5 px-3 transition-all ${activeTab === tab.id ? 'text-amber-500 scale-110' : 'text-indigo-200 active:text-amber-400'}`}
          >
            <tab.icon size={22} className={activeTab === tab.id ? 'drop-shadow-[0_0_10px_rgba(245,158,11,0.6)]' : 'drop-shadow-[0_0_10px_rgba(129,140,248,0.3)]'} />
            <span className="text-[9px] uppercase tracking-[0.2em] font-bold">{tab.label}</span>
          </button>
        ))}
        <button onClick={onBookReading} className="flex flex-col items-center justify-center gap-1.5 px-3 text-amber-500 active:scale-90">
          <div className="p-3 bg-amber-500 text-slate-950 rounded-full -mt-10 shadow-[0_0_25px_rgba(245,158,11,0.6)] border-4 border-[#020617] ring-2 ring-amber-500/20"><Calendar size={26} /></div>
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold mt-1">Book</span>
        </button>
        <button 
          onClick={() => { setActiveTab('profile'); setActivePlacement(null); setActiveBond(null); }} 
          className={`flex flex-col items-center justify-center gap-1.5 px-3 transition-all ${activeTab === 'profile' ? 'text-amber-500 scale-110' : 'text-indigo-200 active:text-amber-400'}`}
        >
          <Settings size={22} className={activeTab === 'profile' ? 'drop-shadow-[0_0_10px_rgba(245,158,11,0.6)]' : 'drop-shadow-[0_0_10px_rgba(129,140,248,0.3)]'} />
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold">Menu</span>
        </button>
      </nav>

      <main className="flex-1 overflow-hidden w-full relative">
        <div className="absolute inset-0 overflow-y-auto p-4 md:p-8 custom-scrollbar pb-32 lg:pb-8">
          
          {activeTab === 'home' && (
            <div className="max-w-6xl mx-auto space-y-8 animate-fade-in text-left">
              <div className="grid lg:grid-cols-12 gap-8 items-stretch">
                <div className="lg:col-span-8 space-y-8">
                   <div id="portal-welcome-card" className="relative overflow-hidden bg-gradient-to-br from-indigo-950 to-slate-900 border border-indigo-900/50 p-8 rounded-sm shadow-2xl flex flex-col justify-center gap-6">
                      <div className="relative z-10">
                         <h1 className="text-3xl md:text-5xl font-serif text-white mb-2 leading-tight">Greetings, <span className="text-amber-400">{member.name.split(' ')[0]}</span>.</h1>
                         <p className="text-indigo-200/70 text-sm max-w-lg italic mb-6">"Aligning with the cosmic cycle of {new Date().toLocaleDateString(undefined, { weekday: 'long' })}."</p>
                         <div className="flex flex-wrap gap-4">
                            <button onClick={() => setActiveTab('my-chart')} className="px-6 py-3 bg-indigo-600 text-white text-[10px] uppercase tracking-widest font-bold rounded-sm hover:bg-indigo-500 transition-all flex items-center gap-2 shadow-lg">Soul Map <ArrowRight size={14}/></button>
                            <button onClick={() => setActiveTab('vault')} className="px-6 py-3 border border-amber-500 text-amber-500 text-[10px] uppercase tracking-widest font-bold rounded-sm hover:bg-amber-500 hover:text-slate-950 transition-all flex items-center gap-2">The Vault <Lock size={14}/></button>
                         </div>
                      </div>
                      <div className="absolute top-0 right-0 p-8 opacity-5"><Orbit size={120} className="text-amber-500" /></div>
                   </div>

                   <div className="grid md:grid-cols-2 gap-8">
                      <div id="portal-daily-illumination" className="bg-slate-900/60 border border-indigo-900/50 p-8 rounded-sm hover:border-amber-500/30 transition-all text-left">
                         <div className="flex justify-between items-center mb-6"><h4 className="text-amber-400 uppercase tracking-[0.3em] text-[10px] font-bold flex items-center gap-2"><Sparkles size={14}/> Daily Illumination</h4></div>
                         {horoscopes.daily ? <p className="text-indigo-50 text-base font-light italic leading-relaxed animate-fade-in">"{horoscopes.daily}"</p> : <div className="text-indigo-500 italic text-xs py-4 flex gap-2"><Loader2 size={12} className="animate-spin"/> Syncing...</div>}
                      </div>
                      <div id="portal-stability-info" className="bg-slate-900/60 border border-indigo-900/50 p-8 rounded-sm hover:border-amber-500/30 transition-all text-left">
                         <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-amber-500/10 rounded-full text-amber-500"><ShieldCheck size={18}/></div><h4 className="text-xl font-serif text-white uppercase tracking-widest">{t.portalHome.blueprintTitle}</h4></div>
                         <p className="text-indigo-100/80 text-sm leading-relaxed font-light italic">{t.portalHome.blueprintDesc}</p>
                      </div>
                   </div>
                </div>

                <div className="lg:col-span-4 space-y-8">
                   {memberChart && <MiniChartWidget chart={memberChart} lang={lang} />}
                   
                   <div id="portal-quick-actions" className="bg-slate-950/40 border border-indigo-900/30 p-6 rounded-sm space-y-4">
                      <h4 className="text-[10px] uppercase font-bold tracking-widest text-indigo-500 mb-4">Quick Alignment</h4>
                      <button onClick={onBookReading} className="w-full flex items-center justify-between p-4 bg-slate-900 border border-indigo-900/50 hover:border-amber-500 transition-all rounded-sm text-xs font-bold uppercase tracking-widest text-white">
                         <span>Book Session</span> <Calendar size={14} className="text-amber-500" />
                      </button>
                      <button onClick={() => setActiveTab('bonds')} className="w-full flex items-center justify-between p-4 bg-slate-900 border border-indigo-900/50 hover:border-amber-500 transition-all rounded-sm text-xs font-bold uppercase tracking-widest text-white">
                         <span>Celestial Bonds</span> <Heart size={14} className="text-indigo-400" />
                      </button>
                   </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'vault' && (
            <div className="max-w-6xl mx-auto space-y-12 animate-fade-in text-left">
               <div className="flex flex-col md:flex-row justify-between items-end border-b border-indigo-900/30 pb-6 gap-6">
                   <div className="text-left">
                      <h3 className="text-4xl font-serif text-white uppercase tracking-widest">The Soul Vault</h3>
                      <p className="text-indigo-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-2">Unlock fated points and celestial mysteries</p>
                   </div>
                </div>
                <VertexFortuneCalculator member={member} onSuccess={(res) => setMember({ ...member, vertex_fortune: res })} lang={lang} />
            </div>
          )}

          {activeTab === 'bonds' && (
            <div className="max-w-6xl mx-auto space-y-12 animate-fade-in text-left pb-20">
               {activeBond ? (
                 <div className="space-y-12 animate-fade-in pb-12">
                   <button 
                     onClick={() => { setActiveBond(null); setActiveBondChart(null); }}
                     className="flex items-center gap-2 text-indigo-400 hover:text-amber-400 transition-colors uppercase tracking-[0.2em] text-[10px] font-bold"
                   >
                     <ArrowLeft size={16}/> Back to Bonds Registry
                   </button>

                   <div className="flex justify-between items-end border-b border-indigo-900/30 pb-6">
                     <div>
                       <h3 className="text-4xl font-serif text-white uppercase tracking-widest">{activeBond.name}</h3>
                       <p className="text-indigo-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-2">{activeBond.relationship || 'Bond'} Alignment</p>
                     </div>
                     <div className="text-right flex flex-col items-end">
                       <p className="text-xs text-indigo-200/60 flex items-center gap-2 mb-1"><Calendar size={12}/> {activeBond.birth_date}</p>
                       <p className="text-xs text-indigo-200/60 flex items-center gap-2 mb-1"><Clock size={12}/> {activeBond.birth_time}</p>
                       <p className="text-xs text-indigo-200/60 flex items-center gap-2"><MapPin size={12}/> {activeBond.birth_location}</p>
                     </div>
                   </div>

                   {loadingBondChart ? (
                     <div className="py-24 flex flex-col items-center justify-center gap-6">
                        <Loader2 className="animate-spin text-amber-500" size={48} />
                        <p className="text-xs uppercase tracking-widest text-indigo-400 font-bold">Synchronizing Bond Frequencies...</p>
                     </div>
                   ) : activeBondChart ? (
                     <div className="space-y-16">
                        <div className="grid md:grid-cols-2 gap-8">
                           {activeBondChart.placements.map((p, i) => (
                             <div key={i} className="bg-slate-900/60 border border-indigo-900/30 p-8 rounded-sm hover:border-amber-500/30 transition-all flex flex-col gap-6">
                                <div className="flex items-center justify-between">
                                   <div className="flex items-center gap-4">
                                      <span className="text-4xl text-amber-500 drop-shadow-[0_0_10px_rgba(245,158,11,0.3)]">{PLANET_SYMBOLS[p.planet] || '✨'}</span>
                                      <div>
                                         <h5 className="text-xl font-serif text-white">{p.planet}</h5>
                                         <p className="text-[10px] uppercase text-indigo-500 font-bold">{p.degree}° {p.sign}</p>
                                      </div>
                                   </div>
                                   <div className="px-3 py-1 bg-amber-500/10 text-amber-500 text-[8px] uppercase font-bold tracking-widest rounded-full border border-amber-500/20">
                                      {PLANET_ARCHETYPES[p.planet]?.keyword || 'PRINCIPLE'}
                                   </div>
                                </div>
                                <div className="p-4 bg-slate-950/40 border-l-2 border-amber-500/50">
                                   <h6 className="text-[10px] uppercase font-bold text-indigo-400 mb-2 flex items-center gap-2">
                                      <BookOpen size={12}/> {PLANET_ARCHETYPES[p.planet]?.title || 'Universal Force'}
                                   </h6>
                                   <p className="text-xs text-indigo-100/70 italic leading-relaxed">
                                      "{PLANET_ARCHETYPES[p.planet]?.desc || 'A celestial influence in the natal alignment.'}"
                                   </p>
                                </div>
                                <p className="text-sm text-indigo-50 leading-relaxed font-light first-letter:text-2xl first-letter:font-serif first-letter:text-amber-400">
                                   {p.description}
                                </p>
                             </div>
                           ))}
                        </div>

                        <div className="pt-8 border-t border-indigo-900/30">
                           <h4 className="text-amber-500 text-[11px] uppercase tracking-[0.4em] font-bold mb-8">Life Arenas (Houses)</h4>
                           <div className="grid md:grid-cols-2 gap-8">
                              {activeBondChart.houses.map((h, i) => (
                                <div key={i} className="bg-slate-900/60 border border-indigo-900/30 p-8 rounded-sm hover:border-indigo-400/30 transition-all flex flex-col gap-6">
                                   <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-4">
                                         <div className="w-12 h-12 flex items-center justify-center bg-indigo-950 border border-indigo-800 text-amber-400 font-serif text-xl rounded-sm">
                                            {h.houseNumber}
                                         </div>
                                         <div>
                                            <h5 className="text-lg font-serif text-white">House {h.houseNumber}</h5>
                                            <p className="text-[10px] uppercase text-indigo-500 font-bold">{h.sign}</p>
                                         </div>
                                      </div>
                                      <div className="px-3 py-1 bg-indigo-500/10 text-indigo-400 text-[8px] uppercase font-bold tracking-widest rounded-full border border-indigo-500/20">
                                         {HOUSE_ARENAS[h.houseNumber]?.domain || 'ARENA'}
                                      </div>
                                   </div>
                                   <div className="p-4 bg-slate-950/40 border-l-2 border-amber-500/50">
                                      <h6 className="text-[10px] uppercase font-bold text-indigo-400 mb-2 flex items-center gap-2">
                                         <MapPin size={12}/> {HOUSE_ARENAS[h.houseNumber]?.title || 'Sacred Space'}
                                      </h6>
                                      <p className="text-xs text-indigo-100/70 italic leading-relaxed">
                                         "{HOUSE_ARENAS[h.houseNumber]?.desc || 'An area of manifestation in this alignment.'}"
                                      </p>
                                   </div>
                                   <p className="text-sm text-indigo-50 leading-relaxed font-light">
                                      {h.description}
                                   </p>
                                </div>
                              ))}
                           </div>
                        </div>
                     </div>
                   ) : null}
                 </div>
               ) : (
                 <>
                   <div className="border-b border-indigo-900/30 pb-6 flex justify-between items-end">
                      <div>
                        <h3 className="text-4xl font-serif text-white uppercase tracking-widest">Celestial Bonds</h3>
                        <p className="text-indigo-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-2">Manage saved charts for friends, family, and synastry</p>
                      </div>
                      <button onClick={() => { setIsAddingBond(true); setEditingBondId(null); setBondForm({ name: '', birth_date: '', birth_time: '', birth_location: '', relationship: '', lat: undefined, lng: undefined }); }} className="px-6 py-3 bg-amber-500 text-slate-950 text-[10px] uppercase font-bold tracking-widest rounded-sm flex items-center gap-2 shadow-lg"><Plus size={16}/> New Bond</button>
                   </div>

                   {isAddingBond && (
                     <div className="bg-slate-900 border border-amber-500/30 p-8 rounded-sm animate-fade-in mb-12 shadow-2xl relative overflow-visible z-[500]">
                       <div className="flex justify-between items-center mb-8">
                         <h4 className="text-xl font-serif text-white uppercase tracking-widest">{editingBondId ? 'Recalibrate Bond' : 'Initiate New Bond'}</h4>
                         <button onClick={() => { setIsAddingBond(false); setEditingBondId(null); setShowBondSuggestions(false); }} className="text-indigo-500"><X size={20}/></button>
                       </div>
                       <form onSubmit={handleBondFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                          <div className="col-span-2 md:col-span-1">
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Bond Name</label>
                            <input required type="text" value={bondForm.name} onChange={e => setBondForm({...bondForm, name: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none" placeholder="Full Name" />
                          </div>
                          <div className="col-span-2 md:col-span-1">
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Relationship</label>
                            <input type="text" value={bondForm.relationship} onChange={e => setBondForm({...bondForm, relationship: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none" placeholder="Friend, Partner, etc." />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Birth Date</label>
                            <input required type="text" placeholder="YYYY-MM-DD" value={bondForm.birth_date} onChange={e => setBondForm({...bondForm, birth_date: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Birth Time (24h)</label>
                            <input 
                              required 
                              type="time" 
                              value={bondForm.birth_time} 
                              onChange={e => setBondForm({...bondForm, birth_time: e.target.value})} 
                              className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none [color-scheme:dark]" 
                            />
                          </div>
                          <div className="col-span-2 relative">
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Birth Location {isSearchingBondLocation && <Loader2 size={10} className="animate-spin text-amber-500 inline ml-2" />}</label>
                            <input 
                              required 
                              type="text" 
                              value={bondForm.birth_location} 
                              autoComplete="off"
                              onFocus={() => bondLocationSuggestions.length > 0 && !preventNextBondSuggestion.current && setShowBondSuggestions(true)}
                              onChange={e => setBondForm({...bondForm, birth_location: e.target.value})} 
                              className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none" 
                              placeholder="City, Country" 
                            />
                            {showBondSuggestions && (
                              <div className="absolute z-[600] w-full mt-2 bg-slate-950 border border-amber-500/50 shadow-2xl rounded-sm overflow-hidden animate-fade-in">
                                <ul className="max-h-48 overflow-y-auto">
                                  {bondLocationSuggestions.map((s, idx) => (
                                    <li key={idx}>
                                      <button 
                                        type="button" 
                                        onClick={() => { 
                                          preventNextBondSuggestion.current = true;
                                          setBondForm({...bondForm, birth_location: s.name, lat: s.lat, lng: s.lng}); 
                                          setShowBondSuggestions(false); 
                                        }} 
                                        className="w-full text-left px-4 py-3 text-[11px] uppercase tracking-wider text-indigo-100 hover:bg-amber-500 hover:text-slate-950 transition-colors flex justify-between items-center"
                                      >
                                        <span>{s.name}</span>
                                        <span className="text-[8px] opacity-40 font-mono">{s.lat.toFixed(2)} / {s.lng.toFixed(2)}</span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          <div className="col-span-2 flex justify-end gap-4 pt-4">
                            <button type="button" onClick={() => { setIsAddingBond(false); setEditingBondId(null); setShowBondSuggestions(false); }} className="px-6 py-3 text-indigo-400 text-[10px] font-bold uppercase">Cancel</button>
                            <button 
                              type="submit" 
                              disabled={bondForm.lat === undefined}
                              className="px-10 py-3 bg-amber-500 text-slate-950 text-[10px] font-bold uppercase rounded-sm shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {editingBondId ? 'Recalibrate' : 'Form Bond'}
                            </button>
                          </div>
                       </form>
                     </div>
                   )}

                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {(member.bonds || []).map(bond => (
                        <div key={bond.id} className="bg-slate-900 border border-indigo-900/30 p-6 rounded-sm flex flex-col relative group overflow-hidden shadow-lg hover:border-amber-500/30 transition-all">
                           <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <button onClick={() => handleEditBond(bond)} className="text-amber-500 hover:text-amber-400"><Edit2 size={16}/></button>
                              <button onClick={() => handleDeleteBond(bond.id)} className="text-rose-500 hover:text-rose-400"><Trash2 size={16}/></button>
                           </div>
                           <div className="flex items-center gap-4 mb-6">
                              <div className="p-3 bg-indigo-950 rounded-full border border-indigo-500/30 text-indigo-400"><User size={20}/></div>
                              <div><h4 className="text-lg font-serif text-white">{bond.name}</h4><p className="text-[10px] uppercase text-indigo-500 font-bold tracking-widest">{bond.relationship || 'Unspecified'}</p></div>
                           </div>
                           <div className="space-y-2 mb-6">
                              <p className="text-xs text-indigo-200/60 flex items-center gap-2 mb-1"><Calendar size={12}/> {bond.birth_date}</p>
                              <p className="text-xs text-indigo-200/60 flex items-center gap-2 mb-1"><Clock size={12}/> {bond.birth_time}</p>
                              <p className="text-xs text-indigo-200/60 flex items-center gap-2"><MapPin size={12}/> {bond.birth_location}</p>
                           </div>
                           <button onClick={() => handleInspectBond(bond)} className="w-full py-3 bg-indigo-600/10 border border-indigo-500/30 text-indigo-100 text-[9px] uppercase font-bold tracking-widest rounded-sm hover:bg-indigo-600 hover:text-white transition-all">Inspect Alignment</button>
                        </div>
                      ))}
                      {(member.bonds || []).length === 0 && !isAddingBond && (
                        <div className="col-span-full py-20 text-center border-2 border-dashed border-indigo-900/30 rounded-sm">
                           <Heart size={40} className="mx-auto text-indigo-950 mb-4" />
                           <p className="text-indigo-400 text-xs uppercase tracking-widest">No Celestial Bonds recorded</p>
                        </div>
                      )}
                   </div>
                 </>
               )}
            </div>
          )}

          {activeTab === 'my-chart' && (
            <div id="portal-chart-view" className="max-w-6xl mx-auto space-y-12 animate-fade-in pb-20 text-left">
               {activePlacement ? (
                 <div className="space-y-12 animate-fade-in">
                    <button 
                      onClick={() => setActivePlacement(null)}
                      className="flex items-center gap-2 text-indigo-400 hover:text-amber-400 transition-colors uppercase tracking-[0.2em] text-[10px] font-bold"
                    >
                      <ArrowLeft size={16}/> Back to Chart Registry
                    </button>

                    <div className="grid lg:grid-cols-12 gap-12">
                       <div className="lg:col-span-4">
                          <div className="bg-slate-900 border border-amber-500/20 p-10 rounded-sm text-center relative overflow-hidden group shadow-2xl">
                             <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent pointer-events-none" />
                             
                             <div className="relative z-10">
                                {activePlacement.type === 'planet' ? (
                                   <>
                                      <div className="text-6xl md:text-8xl text-amber-500 mb-6 drop-shadow-[0_0_20px_rgba(245,158,11,0.4)]">
                                         {PLANET_SYMBOLS[activePlacement.data.planet] || '✨'}
                                      </div>
                                      <h3 className="text-4xl font-serif text-white uppercase tracking-widest mb-2">{activePlacement.data.planet}</h3>
                                      <p className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-bold mb-8">Personalized Archetype</p>
                                      <div className="inline-flex flex-col items-center gap-2 px-6 py-4 bg-indigo-950/40 border border-indigo-900/50 rounded-sm">
                                         <p className="text-[10px] text-indigo-500 uppercase tracking-widest font-bold">Zodiac Degree</p>
                                         <p className="text-xl font-serif text-white">{activePlacement.data.degree}° {activePlacement.data.minutes}' {activePlacement.data.sign}</p>
                                      </div>
                                   </>
                                ) : (
                                   <>
                                      <div className="text-6xl md:text-8xl font-serif text-amber-500 mb-6 drop-shadow-[0_0_20px_rgba(245,158,11,0.4)]">
                                         {activePlacement.data.houseNumber}
                                      </div>
                                      <h3 className="text-4xl font-serif text-white uppercase tracking-widest mb-2">House {activePlacement.data.houseNumber}</h3>
                                      <p className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-bold mb-8">Personalized Life Arena</p>
                                      <div className="inline-flex flex-col items-center gap-2 px-6 py-4 bg-indigo-950/40 border border-indigo-900/50 rounded-sm">
                                         <p className="text-[10px] text-indigo-500 uppercase tracking-widest font-bold">Cusp Sign</p>
                                         <p className="text-xl font-serif text-white">{activePlacement.data.sign}</p>
                                      </div>
                                   </>
                                )}
                             </div>
                          </div>
                       </div>

                       <div className="lg:col-span-8 space-y-10">
                          {/* General Educational Context */}
                          <div className="bg-indigo-950/20 border-l-4 border-amber-500 p-8 rounded-sm">
                             <h4 className="text-amber-500 text-[11px] uppercase tracking-[0.4em] font-bold mb-4 flex items-center gap-3">
                                <BookOpen size={16}/> The Wisdom Principle
                             </h4>
                             <h5 className="text-2xl font-serif text-white mb-4">
                                {activePlacement.type === 'planet' 
                                  ? (PLANET_ARCHETYPES[activePlacement.data.planet]?.title || "Universal Force")
                                  : (HOUSE_ARENAS[activePlacement.data.houseNumber]?.title || "Sacred Space")}
                             </h5>
                             <p className="text-indigo-100/80 leading-relaxed font-light italic text-lg">
                                "{activePlacement.type === 'planet' 
                                  ? (PLANET_ARCHETYPES[activePlacement.data.planet]?.desc || "One of the celestial pillars governing your inner architecture.")
                                  : (HOUSE_ARENAS[activePlacement.data.houseNumber]?.desc || "An area of human experience where your soul seeks to manifest.")}"
                             </p>
                             <div className="mt-6 flex items-center gap-2">
                                <div className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-[9px] uppercase font-bold text-amber-500 tracking-widest">
                                   Governing: {activePlacement.type === 'planet' 
                                     ? (PLANET_ARCHETYPES[activePlacement.data.planet]?.keyword || "EXISTENCE")
                                     : (HOUSE_ARENAS[activePlacement.data.houseNumber]?.domain || "LIFE")}
                                </div>
                             </div>
                          </div>

                          {/* The Deep Personalized Reading */}
                          <div className="space-y-6">
                             <h4 className="text-indigo-400 text-[11px] uppercase tracking-[0.4em] font-bold flex items-center gap-3">
                                <Sparkles size={16}/> Your Sacred Alignment
                             </h4>
                             <div className="relative">
                                <div className="absolute -left-6 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-500 to-transparent" />
                                <p className="text-xl md:text-2xl text-indigo-50 leading-relaxed font-light first-letter:text-4xl first-letter:font-serif first-letter:text-amber-400">
                                   {activePlacement.data.description}
                                </p>
                             </div>
                          </div>

                          <div className="pt-8 border-t border-indigo-900/30 grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div className="p-6 bg-slate-900/40 border border-indigo-900/50 rounded-sm">
                                <h5 className="text-[10px] uppercase text-indigo-500 font-bold mb-4">Practical Application</h5>
                                <p className="text-xs text-indigo-200/60 leading-relaxed">
                                   {activePlacement.type === 'planet' 
                                     ? "Observe how this planet acts during transits to your natal degree. This is a primary driver of your personality."
                                     : "Events occurring in this area of your life will always be flavored by the qualities of the cusp sign shown here."}
                                </p>
                             </div>
                             <div className="p-6 bg-slate-900/40 border border-indigo-900/50 rounded-sm">
                                <h5 className="text-[10px] uppercase text-indigo-500 font-bold mb-4">Soul Task</h5>
                                <p className="text-xs text-indigo-200/60 leading-relaxed italic">
                                   {activePlacement.type === 'planet'
                                     ? "Integrate the strength of your sign placement without letting the shadow traits dominate your psyche."
                                     : "Learn to navigate this domain of life with the maturity and wisdom reflected in your personalized reading."}
                                </p>
                             </div>
                          </div>
                       </div>
                    </div>
                 </div>
               ) : (
                 <>
                   <div className="border-b border-indigo-900/30 pb-6 flex flex-col md:flex-row justify-between items-end gap-6">
                      <div className="text-left">
                        <div className="flex items-center gap-3 mb-2">
                           <h3 className="text-4xl font-serif text-white uppercase tracking-widest">Your Soul Map</h3>
                           {member?.calculated_chart && (
                              <div className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center gap-1.5 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                                 <ShieldCheck size={12} className="text-emerald-500" />
                                 <span className="text-[8px] uppercase font-bold text-emerald-400 tracking-widest">Registry Verified</span>
                              </div>
                           )}
                           {isSyncingWithRegistry && (
                              <div className="flex items-center gap-2 text-[8px] uppercase font-bold text-amber-500 animate-pulse">
                                 <RefreshCw size={10} className="animate-spin" /> Syncing Vault...
                              </div>
                           )}
                        </div>
                        <p className="text-indigo-400 text-[10px] uppercase tracking-[0.3em] font-bold">Select a placement to explore deep interpretations</p>
                      </div>
                      <div className="flex flex-col md:flex-row items-end gap-4">
                        <button 
                           onClick={() => fetchMyChart(member || undefined)} 
                           className="flex items-center gap-2 text-indigo-500 hover:text-amber-500 transition-colors text-[9px] uppercase font-bold tracking-widest mb-1 group"
                        >
                           <RefreshCw size={12} className="group-hover:rotate-180 transition-transform duration-500" /> Recalibrate Alignment
                        </button>
                        <div className="flex bg-indigo-950/30 p-1 rounded-sm border border-indigo-900/50">
                           <button onClick={() => setChartSubTab('planets')} className={`px-6 py-2 text-[10px] uppercase font-bold tracking-widest rounded-sm transition-all ${chartSubTab === 'planets' ? 'bg-amber-500 text-slate-950 shadow-lg' : 'text-indigo-400 hover:text-white'}`}>Planets</button>
                           <button onClick={() => setChartSubTab('houses')} className={`px-6 py-2 text-[10px] uppercase font-bold tracking-widest rounded-sm transition-all ${chartSubTab === 'houses' ? 'bg-amber-500 text-slate-950 shadow-lg' : 'text-indigo-400 hover:text-white'}`}>Houses</button>
                        </div>
                      </div>
                   </div>
                   
                   {memberChart ? (
                     <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-12">
                           {chartSubTab === 'planets' ? (
                              <div className="grid sm:grid-cols-2 gap-4 animate-fade-in">
                                {memberChart.placements.map((p, i) => (
                                   <button 
                                     key={i} 
                                     onClick={() => setActivePlacement({ type: 'planet', data: p })}
                                     className="bg-slate-900/40 border border-indigo-900/30 p-6 rounded-sm text-left hover:border-amber-500/50 transition-all group flex items-center justify-between"
                                   >
                                      <div className="flex items-center gap-6">
                                         <span className="text-3xl text-amber-400 group-hover:scale-110 transition-transform">{PLANET_SYMBOLS[p.planet] || '✨'}</span>
                                         <div>
                                            <h5 className="text-[9px] uppercase text-indigo-500 font-bold mb-1">{p.planet}</h5>
                                            <p className="text-lg font-serif text-white">{p.degree}° {p.sign}</p>
                                         </div>
                                      </div>
                                      <ChevronRight size={20} className="text-indigo-900 group-hover:text-amber-500 transition-colors" />
                                   </button>
                                ))}
                              </div>
                           ) : (
                              <div className="grid sm:grid-cols-2 gap-4 animate-fade-in">
                                {memberChart.houses.map((h, i) => (
                                   <button 
                                     key={i} 
                                     onClick={() => setActivePlacement({ type: 'house', data: h })}
                                     className="bg-slate-900/40 border border-indigo-900/30 p-6 rounded-sm text-left hover:border-amber-500/50 transition-all group flex items-center justify-between"
                                   >
                                      <div className="flex items-center gap-6">
                                         <div className="w-10 h-10 flex items-center justify-center bg-indigo-950 border border-indigo-800 text-amber-400 font-serif text-lg rounded-sm group-hover:bg-amber-500 group-hover:text-slate-950 transition-colors">
                                            {h.houseNumber}
                                         </div>
                                         <div>
                                            <h5 className="text-[9px] uppercase text-indigo-500 font-bold mb-1">House {h.houseNumber}</h5>
                                            <p className="text-lg font-serif text-white">{h.sign}</p>
                                         </div>
                                      </div>
                                      <ChevronRight size={20} className="text-indigo-900 group-hover:text-amber-500 transition-colors" />
                                   </button>
                                ))}
                              </div>
                           )}
                        </div>
                     </div>
                   ) : (
                     <div className="flex flex-col items-center justify-center py-32 space-y-6">
                        <Loader2 className="animate-spin text-amber-500" size={48} />
                        <p className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-bold">Synchronizing Soul Map...</p>
                     </div>
                   )}
                 </>
               )}
            </div>
          )}

          {activeTab === 'messages' && (
             <div id="portal-chat-section" className="max-w-4xl mx-auto h-[calc(100vh-250px)] flex flex-col bg-slate-900 border border-indigo-900/50 rounded-sm overflow-hidden shadow-2xl animate-fade-in text-left">
               {!hasUnlockedChat ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-slate-950/80">
                    <Lock size={40} className="text-amber-500/60 mb-8" />
                    <h3 className="text-3xl font-serif text-white mb-4 uppercase tracking-widest">{t.chat.lockedTitle}</h3>
                    <p className="text-indigo-200/60 text-sm max-w-sm mb-12 italic">"{t.chat.lockedDesc}"</p>
                    <button onClick={onBookReading} className="px-12 py-5 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-amber-400 transition-all">View Offerings</button>
                  </div>
               ) : (
                 <>
                   <div className="p-4 bg-slate-950 border-b border-indigo-900/30 flex justify-between items-center"><h3 className="text-lg font-serif text-white uppercase tracking-widest">Channel with Allie</h3></div>
                   <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-950/30">
                     {messages.map((m, idx) => (
                         <div key={idx} className={`flex ${m.sender_id === 'admin' ? 'justify-start' : 'justify-end'}`}>
                           <div className={`p-4 rounded-sm max-w-[80%] text-sm ${m.sender_id === 'admin' ? 'bg-indigo-900/60 text-white' : 'bg-slate-800 text-amber-50 border border-amber-500/20'}`}>
                             {m.content}
                           </div>
                         </div>
                       ))}
                     <div ref={messagesEndRef} />
                   </div>
                   <form onSubmit={handleSendMessage} className="p-4 bg-slate-950 border-t border-indigo-900/50 flex gap-4">
                     <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Compose soul transmission..." className="flex-1 bg-slate-900 border border-indigo-900/50 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none" />
                     <button type="submit" disabled={sendingMessage || !newMessage.trim()} className="px-8 bg-amber-500 text-slate-950 rounded-sm font-bold uppercase text-[10px] tracking-widest">Send</button>
                   </form>
                 </>
               )}
            </div>
          )}

          {activeTab === 'bookings' && (
             <div className="max-w-6xl mx-auto space-y-8 animate-fade-in text-left">
                <div className="border-b border-indigo-900/30 pb-4"><h3 className="text-3xl font-serif text-white uppercase tracking-widest">Your Appointments</h3></div>
                <div className="grid gap-4">
                  {memberBookings.map(b => (
                    <div key={b.id} className="bg-slate-900 border border-indigo-900/50 p-6 rounded-sm flex items-center justify-between">
                       <div><h4 className="text-white font-serif uppercase tracking-widest">{b.commMethod} Session</h4><p className="text-xs text-indigo-400">{b.appointment_date} @ {b.appointment_time}</p></div>
                       <div className="px-4 py-1.5 rounded-full text-[9px] uppercase font-bold tracking-widest border bg-amber-500/10 border-amber-500/30 text-amber-500">{b.status}</div>
                    </div>
                  ))}
                  {memberBookings.length === 0 && <p className="text-indigo-500 italic py-10">No sessions recorded in this cycle.</p>}
                </div>
             </div>
          )}

          {activeTab === 'profile' && (
            <div className="max-w-5xl mx-auto space-y-12 animate-fade-in text-left pb-20">
               <div className="border-b border-indigo-900/30 pb-6">
                 <h3 className="text-4xl font-serif text-white uppercase tracking-widest">Registry Settings</h3>
                 <p className="text-indigo-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-2">Manage your soul identity and sanctuary preferences</p>
               </div>
               
               <form onSubmit={handleSaveProfile} className="space-y-12">
                  <div className="bg-slate-900/60 border border-indigo-900/50 p-8 rounded-sm space-y-8">
                    <div className="flex items-center gap-3 mb-4">
                      <User size={18} className="text-amber-500" />
                      <h4 className="text-xl font-serif text-white uppercase tracking-widest">Soul Identity</h4>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-8">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Public Soul Name</label>
                        <input required type="text" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm focus:border-amber-500 outline-none transition-all" />
                      </div>
                      
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Soul Reflection (Bio)</label>
                        <textarea value={profileForm.bio} onChange={e => setProfileForm({...profileForm, bio: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm h-32 focus:border-amber-500 outline-none transition-all" placeholder="Share your cosmic intent..." />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Earthly Location</label>
                        <input type="text" value={profileForm.location} onChange={e => setProfileForm({...profileForm, location: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="City, Country" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/60 border border-indigo-900/50 p-8 rounded-sm space-y-8">
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin size={18} className="text-amber-500" />
                      <h4 className="text-xl font-serif text-white uppercase tracking-widest">Precise Origins</h4>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Birth Date</label>
                        <input required type="text" value={profileForm.birth_date} onChange={e => setProfileForm({...profileForm, birth_date: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm focus:border-amber-500 outline-none" placeholder="YYYY-MM-DD" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Birth Time (24h)</label>
                        <input required type="text" value={profileForm.birth_time} onChange={e => setProfileForm({...profileForm, birth_time: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm focus:border-amber-500 outline-none" placeholder="HH:MM" />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-1">
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-3">Birth Coordinates (City)</label>
                        <input required type="text" value={profileForm.birth_location} onChange={e => setProfileForm({...profileForm, birth_location: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 text-sm rounded-sm focus:border-amber-500 outline-none" placeholder="City of Entry" />
                      </div>
                    </div>
                    <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-sm flex gap-3">
                      <Info size={16} className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-amber-200/50 leading-relaxed">Changing birth details will trigger a re-calculation of your soul map upon saving.</p>
                    </div>
                  </div>

                  <div className="bg-slate-900/60 border border-indigo-900/50 p-8 rounded-sm space-y-10">
                    <div className="flex items-center gap-3">
                      <Settings size={18} className="text-amber-500" />
                      <h4 className="text-xl font-serif text-white uppercase tracking-widest">Sanctuary Preferences</h4>
                    </div>

                    <div className="space-y-6 pt-6">
                      <h5 className="text-[10px] uppercase tracking-[0.2em] text-indigo-400 font-bold">Cosmic Notifications</h5>
                      <div className="space-y-4">
                        <label className="flex items-center justify-between p-4 bg-slate-950/50 border border-indigo-900/30 rounded-sm cursor-pointer hover:border-amber-500/30 transition-all">
                          <div className="flex items-center gap-4">
                            <Orbit size={18} className="text-indigo-400" />
                            <span className="text-sm text-indigo-100">Major Planetary Transits</span>
                          </div>
                          <input type="checkbox" checked={profileForm.notify_transits} onChange={e => setProfileForm({...profileForm, notify_transits: e.target.checked})} className="w-5 h-5 accent-amber-500" />
                        </label>
                        <label className="flex items-center justify-between p-4 bg-slate-950/50 border border-indigo-900/30 rounded-sm cursor-pointer hover:border-amber-500/30 transition-all">
                          <div className="flex items-center gap-4">
                            <Bell size={18} className="text-indigo-400" />
                            <span className="text-sm text-indigo-100">Sanctuary News & Deal Transmissions</span>
                          </div>
                          <input type="checkbox" checked={profileForm.notify_marketing} onChange={e => setProfileForm({...profileForm, notify_marketing: e.target.checked})} className="w-5 h-5 accent-amber-500" />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button type="submit" disabled={isSavingProfile} className="px-12 py-5 bg-amber-500 text-slate-950 font-bold uppercase tracking-widest text-xs rounded-sm shadow-2xl hover:bg-amber-400 transition-all flex items-center gap-3 active:scale-95">
                      {isSavingProfile ? <Loader2 size={18} className="animate-spin" /> : <><ShieldCheck size={18}/> Synchronize Registry</>}
                    </button>
                  </div>
               </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MemberPortal;